import type { ProfileItem, ProfileKind } from './types';

const PROFILE_KINDS = new Set<ProfileKind>(['interest', 'keyword', 'publication', 'summary']);
const DEFAULT_CHUNK_SIZE = 1_500;
const DEFAULT_OVERLAP = 150;

export interface ProfileItemInput {
  id?: string;
  kind: ProfileKind;
  label: string;
  text: string;
  weight?: number;
  sourceFile?: string;
}

export type ProfileItemUpdate = Partial<Omit<ProfileItem, 'id'>>;

export interface ChunkTextOptions {
  maxLength?: number;
  overlap?: number;
}

function assertNonEmpty(value: string, field: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${field} is required.`);
  return trimmed;
}

function validateWeight(weight: number): number {
  if (!Number.isFinite(weight) || weight < 0) {
    throw new Error('Profile item weight must be a finite, non-negative number.');
  }
  return weight;
}

function makeId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `profile-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createProfileItem(input: ProfileItemInput): ProfileItem {
  if (!PROFILE_KINDS.has(input.kind)) throw new Error(`Unsupported profile kind: ${input.kind}`);

  const sourceFile = input.sourceFile?.trim();
  return {
    id: input.id ? assertNonEmpty(input.id, 'Profile item ID') : makeId(),
    kind: input.kind,
    label: assertNonEmpty(input.label, 'Profile item label'),
    text: assertNonEmpty(input.text, 'Profile item text'),
    weight: validateWeight(input.weight ?? 1),
    ...(sourceFile ? { sourceFile } : {}),
  };
}

export function updateProfileItem(
  item: ProfileItem,
  updates: ProfileItemUpdate,
): ProfileItem {
  return createProfileItem({
    id: item.id,
    kind: updates.kind ?? item.kind,
    label: updates.label ?? item.label,
    text: updates.text ?? item.text,
    weight: updates.weight ?? item.weight,
    ...('sourceFile' in updates
      ? { sourceFile: updates.sourceFile }
      : item.sourceFile
        ? { sourceFile: item.sourceFile }
        : {}),
  });
}

function findBreak(text: string, start: number, hardEnd: number): number {
  if (hardEnd >= text.length) return text.length;
  const minimum = start + Math.floor((hardEnd - start) * 0.6);
  const candidates = [
    text.lastIndexOf('\n\n', hardEnd),
    text.lastIndexOf('\n', hardEnd),
    text.lastIndexOf('. ', hardEnd),
    text.lastIndexOf(' ', hardEnd),
  ];
  const best = Math.max(...candidates);
  return best >= minimum
    ? Math.min(hardEnd, best + (text.startsWith('. ', best) ? 1 : 0))
    : hardEnd;
}

/** Split long text on natural boundaries while retaining optional context overlap. */
export function chunkLongText(text: string, options: ChunkTextOptions = {}): string[] {
  const normalized = text.replace(/\r\n?/g, '\n').trim();
  if (!normalized) return [];

  const maxLength = options.maxLength ?? DEFAULT_CHUNK_SIZE;
  const overlap = options.overlap ?? DEFAULT_OVERLAP;
  if (!Number.isInteger(maxLength) || maxLength < 1) {
    throw new Error('Chunk maxLength must be a positive integer.');
  }
  if (!Number.isInteger(overlap) || overlap < 0 || overlap >= maxLength) {
    throw new Error('Chunk overlap must be a non-negative integer smaller than maxLength.');
  }

  const chunks: string[] = [];
  let start = 0;
  while (start < normalized.length) {
    const end = findBreak(normalized, start, Math.min(start + maxLength, normalized.length));
    const chunk = normalized.slice(start, end).trim();
    if (chunk) chunks.push(chunk);
    if (end >= normalized.length) break;

    const nextStart = Math.max(start + 1, end - overlap);
    const whitespace = normalized.indexOf(' ', nextStart);
    start = whitespace !== -1 && whitespace < end ? whitespace + 1 : nextStart;
  }
  return chunks;
}

export function createProfileItemsFromText(
  input: ProfileItemInput,
  options: ChunkTextOptions = {},
): ProfileItem[] {
  const chunks = chunkLongText(input.text, options);
  if (chunks.length <= 1) return [createProfileItem(input)];
  return chunks.map((text, index) => {
    const itemInput: ProfileItemInput = {
      ...input,
      label: `${input.label.trim()} (${index + 1}/${chunks.length})`,
      text,
    };
    if (input.id) itemInput.id = `${input.id}-${index + 1}`;
    return createProfileItem(itemInput);
  });
}
